import { fade, Grid, makeStyles, Theme, Typography } from "@material-ui/core";
import React, { FunctionComponent } from "react";
import { ResilienceLevel } from "../../../../api/smartmelts-api/graphql/generated";
import { EmojiAngry } from "../../../../assets/icons/emojis/emoji-angry";
import { EmojiHappy } from "../../../../assets/icons/emojis/emoji-happy";
import { EmojiNormal } from "../../../../assets/icons/emojis/emoji-normal";
import { EmojiSad } from "../../../../assets/icons/emojis/emoji-sad";
import { EmojiStressed } from "../../../../assets/icons/emojis/emoji-stressed";
import { EmojiWorried } from "../../../../assets/icons/emojis/emoji-worried";
import { IIconProps } from "../../../../assets/icons/icon";
import { RED_COLOR, YELLOW_COLOR } from "../../../../theme/theme";

export interface IResilienceResultBoxStyleProps {
  result: ResilienceLevel;
}

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    padding: `${theme.spacing(4)}px ${theme.spacing(6)}px`,
    border: `2px solid ${theme.palette.grey["100"]}`,
    borderRadius: theme.spacing(4),
    width: "100%",
    maxWidth: 300,
  },
  emojiContainer: {
    paddingTop: theme.spacing(4),
  },
  heading: {
    fontSize: 18,
    fontWeight: 700,
    paddingBottom: theme.spacing(3),
  },
  subheading: {
    fontSize: 14,
    fontWeight: 300,
  },
  emoji: {
    borderRadius: "50%",
    padding: theme.spacing(1),
    width: "100%",
    maxWidth: 40,
    maxHeight: 40,
    opacity: 0.5,
  },
  high: {
    opacity: ({ result }: IResilienceResultBoxStyleProps) => (result === ResilienceLevel.HIGH ? 1 : 0.5),
    background: ({ result }: IResilienceResultBoxStyleProps) =>
      result === ResilienceLevel.HIGH ? fade(theme.palette.primary.main, 0.8) : "transparent",
  },
  medium: {
    opacity: ({ result }: IResilienceResultBoxStyleProps) => (result === ResilienceLevel.NORMAL ? 1 : 0.5),
    background: ({ result }: IResilienceResultBoxStyleProps) =>
      result === ResilienceLevel.NORMAL ? fade(YELLOW_COLOR, 0.8) : "transparent",
  },
  low: {
    opacity: ({ result }: IResilienceResultBoxStyleProps) => (result === ResilienceLevel.LOW ? 1 : 0.5),
    background: ({ result }: IResilienceResultBoxStyleProps) =>
      result === ResilienceLevel.LOW ? fade(RED_COLOR, 0.8) : "transparent",
  },
}));

const ICON_DICTIONARY: Record<ResilienceResultBoxLastIconType, FunctionComponent<IIconProps>> = {
  ANGRY: EmojiAngry,
  SAD: EmojiSad,
  STRESSED: EmojiStressed,
  WORRIED: EmojiWorried,
};

export enum ResilienceResultBoxLastIconType {
  ANGRY = "ANGRY",
  SAD = "SAD",
  STRESSED = "STRESSED",
  WORRIED = "WORRIED",
}

export interface IResilienceResultBoxProps {
  title: string;
  content: string;
  result: ResilienceLevel;
  lastIcon: ResilienceResultBoxLastIconType;
}

export const ResilienceResultBox: FunctionComponent<IResilienceResultBoxProps> = (props) => {
  const { title, content, result, lastIcon } = props;
  const LastIconComponent = ICON_DICTIONARY[lastIcon];
  const classes = useStyles({ result });
  return (
    <Grid container className={classes.container}>
      <Typography className={classes.heading}>{title}</Typography>
      <Typography className={classes.subheading}>{content}</Typography>
      <Grid container spacing={3} className={classes.emojiContainer} justify="space-around">
        <Grid container item xs={4} justify="center">
          <EmojiHappy className={`${classes.emoji} ${classes.high}`} />
        </Grid>
        <Grid container item xs={4} justify="center">
          <EmojiNormal className={`${classes.emoji} ${classes.medium}`} />
        </Grid>
        <Grid container item xs={4} justify="center">
          <LastIconComponent className={`${classes.emoji} ${classes.low}`} />
        </Grid>
      </Grid>
    </Grid>
  );
};
